import {
  IntegrationRequest,
  IntegrationRequestDto,
  Professional,
  SendingRequest,
  SendingRequestDto,
  TrustedContact,
} from '@domain/types';
import { logger } from '@services/logger/logger.service';

import { EntitiesState, EntitiesType } from './entities/entities.state';

export function getEntity(
  professional: {
    type: EntitiesType;
    id: string;
  },
  entities: EntitiesState,
): Professional {
  const entity = entities[professional.type]?.[professional.id] as Professional;
  if (!entity) {
    logger.error('LOGIC', `Could not find entity ${professional.type}/${professional.id}`);
  }
  return entity;
}

/**
 * Compares two names alphabetically (lastName first, then firstName)
 * @returns negative if a < b, positive if a > b, 0 if equal
 */
function compareByName(
  firstNameA: string,
  lastNameA: string,
  firstNameB: string,
  lastNameB: string,
): number {
  const lastA = lastNameA.toLowerCase();
  const lastB = lastNameB.toLowerCase();

  if (lastA !== lastB) {
    return lastA.localeCompare(lastB, 'fr');
  }

  const firstA = firstNameA.toLowerCase();
  const firstB = firstNameB.toLowerCase();

  return firstA.localeCompare(firstB, 'fr');
}

/**
 * Sorts trusted contacts with the following order:
 * 1. Contacts with default=true (sorted alphabetically by lastName + firstName)
 * 2. Other contacts (sorted alphabetically by lastName + firstName)
 */
export function sortTrustedContacts(contacts: TrustedContact[]): TrustedContact[] {
  return [...contacts].sort((a, b) => {
    // 1. Default contacts first
    if (a.default && !b.default) return -1;
    if (!a.default && b.default) return 1;

    // 2. Sort alphabetically by lastName then firstName
    return compareByName(a.firstName || '', a.lastName || '', b.firstName || '', b.lastName || '');
  });
}

/**
 * Sorts recipients so that trusted contact recipients are ordered correctly:
 * 1. Contacts with default=true (sorted alphabetically by lastName + firstName)
 * 2. Other contacts (sorted alphabetically by lastName + firstName)
 * Non-trusted-contact recipients maintain their relative order
 */
export function sortTrustedContactRecipients(
  recipients: SendingRequest['recipients'],
  ehrPatient?: { trustedContacts?: TrustedContact[] },
): SendingRequest['recipients'] {
  const trustedContactRecipients: SendingRequest['recipients'] = [];
  const otherRecipients: SendingRequest['recipients'] = [];

  // Separate trusted contact recipients from others
  recipients.forEach((recipient) => {
    if (recipient.trustedContactId) {
      trustedContactRecipients.push(recipient);
    } else {
      otherRecipients.push(recipient);
    }
  });

  // Sort trusted contact recipients based on the corresponding TrustedContact data
  const sortedTrustedContactRecipients = trustedContactRecipients.sort((a, b) => {
    const contactA = ehrPatient?.trustedContacts?.find((c) => c.id === a.trustedContactId);
    const contactB = ehrPatient?.trustedContacts?.find((c) => c.id === b.trustedContactId);

    // 1. Default contacts first
    if (contactA?.default && !contactB?.default) return -1;
    if (!contactA?.default && contactB?.default) return 1;

    // 2. Sort alphabetically by lastName then firstName
    return compareByName(
      contactA?.firstName || a.name || '',
      contactA?.lastName || '',
      contactB?.firstName || b.name || '',
      contactB?.lastName || '',
    );
  });

  // Return other recipients first, then sorted trusted contact recipients
  return [...otherRecipients, ...sortedTrustedContactRecipients];
}

/* DOCUMENTS */

export function documentDtoToSendingRequest(
  documentDto: SendingRequestDto,
  entities: EntitiesState,
): SendingRequest {
  const { sender, patient, recipients } = documentDto;

  const ehrPatient = patient?.ehrPatientId ? entities.Patient[patient.ehrPatientId] : undefined;
  const sortedEhrPatient = ehrPatient
    ? {
        ...ehrPatient,
        trustedContacts: ehrPatient.trustedContacts
          ? sortTrustedContacts(ehrPatient.trustedContacts)
          : undefined,
      }
    : undefined;

  const mappedRecipients = recipients
    .map((recipientDto) => {
      // For patient trusted contacts, bind entity to ehrPatient to have trustedContacts available
      const shouldBindToEhrPatient =
        recipientDto.type === 'Patient' &&
        !!(recipientDto as any).trustedContactId &&
        !!patient?.ehrPatientId;

      const entity: Professional = shouldBindToEhrPatient
        ? (sortedEhrPatient as unknown as Professional)
        : getEntity(recipientDto, entities);
      return {
        id: recipientDto.id,
        contactId: recipientDto.contactId,
        contactType: recipientDto.contactType,
        type: recipientDto.type,
        entity,
        telecoms: recipientDto.telecoms,
        addresses: recipientDto.addresses,
        prediction: recipientDto.prediction,
        verifications: recipientDto.verifications,
        isFavorite: recipientDto.isFavorite,
        trustedContactRelationship: recipientDto.trustedContactRelationship,
        trustedContactId: recipientDto.trustedContactId,
        name: recipientDto.name,
      };
    })
    // Fixing a rare use case where we don't have the entity - https://honestica.atlassian.net/browse/CORE-3884
    .filter((recipient) => !!recipient.entity);

  const sortedRecipients = sortTrustedContactRecipients(mappedRecipients, sortedEhrPatient);

  return {
    ...documentDto,
    sender: getEntity({ id: sender.id, type: sender.type }, entities),
    patient: patient && {
      ...patient,
      patient: entities.Patient[patient.id],
      ehrPatient: sortedEhrPatient,
    },
    recipients: sortedRecipients,
  };
}

/* INTEGRATIONS */
export function integrationRequestDtoToIntegrationRequest(
  integrationRequestDto: IntegrationRequestDto,
  entities: EntitiesState,
): IntegrationRequest {
  const { sender, originalSenderOrganization, originalSenderPractitioner, patient, source } =
    integrationRequestDto;
  return {
    ...integrationRequestDto,
    attachments: entities.Attachments[integrationRequestDto.id],
    sender: getEntity({ id: sender.id, type: sender.type }, entities),
    originalSenderOrganization,
    originalSenderPractitioner,
    patient: patient && {
      ...patient,
      patient: entities.Patient[patient.id],
      ehrPatient: patient.ehrPatientId ? entities.Patient[patient.ehrPatientId] : undefined,
    },
    source,
  };
}
